import { PlayIcon } from '@giphy/react-components';
import { Tooltip } from 'antd';
import Clipboard from 'clipboard';
import { useSession } from 'next-auth/react';
import { SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EmailIcon,
  FacebookIcon,
  FacebookMessengerIcon,
  FacebookMessengerShareButton,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import styled from 'styled-components';

import { ArrowLeftSvg, QuestionMarkSvg } from '../../../assets/icons';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import PremiumIconFilled from '../../../assets/icons/PremiumIconFilled';
import theme from '../../../assets/theme';
import {
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../hooks/useDesignerLogic';
import { useMetrics } from '../../../hooks/useMetrics';
import usePaywallPrompt from '../../../hooks/usePaywallPrompt';
import { useMarketingMetrics } from '../../../providers/PlausibleAnalyticsProvider';
import { getBaseUrl } from '../../../utils/client';
import { gentlyCheckDestinationTenantUrl } from '../../../utils/navigatorUtils';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import CreateTeam from '../../_organisms/CreateTeam';
import MSTeamsShareButton from '../MSTeamsShareButton';
import QRCodeShareButton from '../QRCodeShareButton';

const CreatorShareComponent = ({
  templateId,
  isGeneratedByAI,
  handlePresent,
  isCreatingGame,
  handleExportAsPdf,
  shouldShowDownloadAsPDF,
  setShouldShowDownloadAsPDF,
  templateTitle,
  isGenerator = false,
}: {
  templateId: string;
  templateTitle: string;
  isGeneratedByAI: boolean;
  isGenerator: boolean;
  handlePresent: () => void;
  isCreatingGame: boolean;
  handleExportAsPdf?: ({
    onSuccess,
    title,
  }: {
    onSuccess: () => void;
    title: string;
  }) => void;
  shouldShowDownloadAsPDF?: boolean;
  setShouldShowDownloadAsPDF?: (value: SetStateAction<boolean>) => void;
}) => {
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const { isReady, promptPaywall, paywallComponent, tenantPlan } = usePaywallPrompt({
    startState: { screen: 'chooseProduct' },
  });
  const templateRef = useRef(new Clipboard('#copy_url_template'));
  const templateJoinRef = useRef(new Clipboard('#copy_join_url_template'));
  const [copyTemplateSuccess, setCopyTemplateSuccess] = useState(false);
  const [copyJoinLinkSuccess, setCopyJoinLinkSuccess] = useState(false);
  const { t } = useTranslation('Host');
  const session = useSession();
  const [showCreateWorkspacePopup, setShowCreateWorkspacePopup] = useState(false);
  const localTemplateDispatch = useDesignerDataContext(context => context.dispatch);
  const metrics = useMetrics();
  const { track } = useMarketingMetrics();

  useEffect(() => {
    const clip = templateRef.current;
    clip.on('success', () => {
      localTemplateDispatch({
        type: TemplateStateActions.isBlockedFromSharingChanged,
        isBlockedFromSharing: false,
      });
      setCopyTemplateSuccess(true);
    });

    return () => {
      clip.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const clip = templateJoinRef.current;
    clip.on('success', () => {
      setCopyJoinLinkSuccess(true);
    });

    return () => {
      clip.destroy();
    };
  }, []);

  useEffect(() => {
    const ref = setTimeout(() => {
      if (copyTemplateSuccess) {
        if (session.status === 'authenticated') {
          metrics.logEvent('Live.Share.Template.Copy.Success', {
            templateId: templateId,
            from: 'designer',
          });
        } else {
          track('Live.Share.Template.Copy.Success', {
            props: {
              templateId: templateId,
              from: 'designer',
            },
          });
        }
        setCopyTemplateSuccess(false);
      }
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [copyTemplateSuccess, metrics, templateId, track, session.status]);
  useEffect(() => {
    const ref = setTimeout(() => {
      if (copyJoinLinkSuccess) {
        metrics.logEvent('Live.Share.StudentJoinLink.Copy.Success', {
          templateId: templateId,
          from: 'designer',
        });

        setCopyJoinLinkSuccess(false);
      }
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [copyJoinLinkSuccess, metrics, templateId, track, session.status]);

  const shareUrl = isGenerator
    ? `${getBaseUrl()}/generators/${templateTitle
        .replace(/\s/g, '-')
        .replace(/[^\w\s\-]/gi, '')}/g/${templateId}`
    : `${getBaseUrl()}/${gentlyCheckDestinationTenantUrl()}/lessons/${templateId}?showpreview=true`;

  const shareTitle = isGeneratedByAI
    ? `${t('This lesson was generated by AI in less than 10 seconds')}`
    : '\n';

  const emailSubject = isGeneratedByAI
    ? t('I generated this lesson in 10 seconds with Curipod')
    : t('Check out my Curipod lesson');

  const handleClickShareButton = useCallback(
    (type: string) => {
      if (session.status === 'authenticated') {
        localTemplateDispatch({
          type: TemplateStateActions.isBlockedFromSharingChanged,
          isBlockedFromSharing: false,
        });
        metrics.logEvent('Share.Social.Media.Click', { someType: type });
      } else {
        track('Share.Social.Media.Click', { props: { someType: type } });
      }
    },
    [metrics, session.status, track, localTemplateDispatch],
  );

  const handleCreateTeamClicked = () => {
    if (!isReady) return;
    if (!tenantPlan?.isEnterprise()) {
      metrics.logEvent('Upgrade.Clicked', { location: 'Create.Team.Designer' });
      promptPaywall();
    } else {
      metrics.logEvent('CreateTeam', { from: 'Share.Lesson.Designer' });
      setShowCreateWorkspacePopup(true);
    }
  };

  const handleDownloadPDFClicked = () => {
    if (!isReady || !handleExportAsPdf) return;
    if (isDownloadingPDF) return;
    if (!tenantPlan?.isEnterprise() && !tenantPlan?.isPremium()) {
      metrics.logEvent('Download.PDF.Designer', { clicked: 'Upgrade' });
      promptPaywall();
    } else {
      setIsDownloadingPDF(true);
      metrics.logEvent('Download.PDF.Designer', { clicked: 'Download' });
      handleExportAsPdf({
        title: templateTitle,
        onSuccess: () => {
          setIsDownloadingPDF(false);
        },
      });
    }
  };

  return (
    <div style={{ width: '100%', padding: 20 }}>
      {paywallComponent}
      {!shouldShowDownloadAsPDF && (
        <>
          <CreateTeam
            onDone={() => setShowCreateWorkspacePopup(false)}
            visible={showCreateWorkspacePopup}
          />
          <h2>
            {isGenerator ? 'Share this customizable lesson' : t('Share this lesson')}
          </h2>
          <Title $fontSize={16}>
            {t('Share with students')}{' '}
            <Tooltip
              title={
                'Share this link with your students, where they can enter the pin and join the lesson'
              }
            >
              <HelpIconWrapper>
                <QuestionMarkSvg />
              </HelpIconWrapper>
            </Tooltip>
          </Title>
          <Button
            id="copy_join_url_template"
            data-clipboard-text={'https://curi.live'}
            styles={{
              background: theme.colors.orange,
              width: '100%',
              marginBottom: '20px',
            }}
          >
            {copyJoinLinkSuccess ? t('Join link copied') : t('Copy join link')}
          </Button>
          {handleExportAsPdf && setShouldShowDownloadAsPDF && (
            <>
              <Divider />
              <DownloadButton onClick={() => setShouldShowDownloadAsPDF(true)}>
                <span>{t('Download as PDF')}</span>
                <IconWrapper color={theme.colors.black} height={'30px'} width={'30px'}>
                  <ArrowRightSvg />
                </IconWrapper>
              </DownloadButton>
              <Divider />
            </>
          )}
          <Title style={{ marginTop: 20 }} $fontSize={16}>
            {t('Share on social')}
          </Title>
          <SocialMediaContainer>
            <TwitterShareButton
              url={shareUrl}
              title={`${shareTitle}`}
              onClick={() => handleClickShareButton('twitter')}
            >
              <TwitterIcon size={32} round={true} />
            </TwitterShareButton>
            <LinkedinShareButton
              url={shareUrl}
              title={shareTitle}
              onClick={() => handleClickShareButton('linkedin')}
            >
              <LinkedinIcon size={32} round={true} />
            </LinkedinShareButton>
            <FacebookShareButton
              url={shareUrl}
              quote={shareTitle}
              onClick={() => handleClickShareButton('facebook')}
            >
              <FacebookIcon size={32} round={true} />
            </FacebookShareButton>
            <FacebookMessengerShareButton
              url={shareUrl}
              title={shareTitle}
              appId="857396192234237"
              onClick={() => handleClickShareButton('facebook')}
            >
              <FacebookMessengerIcon size={32} round={true} />
            </FacebookMessengerShareButton>
            <QRCodeShareButton url={shareUrl} />
            <a
              href={`mailto:?subject=${emailSubject}&body=${t(
                'Below is a link to my Curipod lesson.',
              )}\n\n\n ${shareUrl}`}
              onClick={() => handleClickShareButton('email')}
            >
              <EmailIcon size={32} round={true} />
            </a>
            <WhatsappShareButton
              url={shareUrl}
              title={shareTitle}
              onClick={() => handleClickShareButton('whatsapp')}
            >
              <WhatsappIcon size={32} round={true} />
            </WhatsappShareButton>
            <MSTeamsShareButton url={shareUrl} body={shareTitle} />
          </SocialMediaContainer>
          <Divider />
          <ShareMenuItemContainer>
            <Title $fontSize={16}>
              {t('Share with teachers')}{' '}
              <Tooltip
                title={
                  isGenerator
                    ? 'Anyone with the link to the customizable lesson will be able to consume it. Please remember to publish the customizable lesson before sharing.'
                    : t(
                        'Anyone with access to this lesson can make a copy and continue to edit. Your lesson will always remain unchanged',
                      )
                }
              >
                <HelpIconWrapper>
                  <QuestionMarkSvg />
                </HelpIconWrapper>
              </Tooltip>
            </Title>
            <Button
              id="copy_url_template"
              data-clipboard-text={shareUrl}
              width="100%"
              styles={{
                color: theme.colors.white,
                backgroundColor: theme.colors.blue,
              }}
            >
              {copyTemplateSuccess ? t('Link copied') : t('Copy link')}
            </Button>
          </ShareMenuItemContainer>
          {tenantPlan?.isEnterprise() && !tenantPlan?.isTrial() && (
            <ShareMenuItemContainer>
              <Title style={{ marginBottom: 0 }} $fontSize={16}>
                {t('Share your lessons with your team')}{' '}
                <Tooltip
                  title={t(
                    'Create a shared workspace for your team to collaborate on creating amazing lessons. Available on school plans.',
                  )}
                >
                  <HelpIconWrapper>
                    <QuestionMarkSvg />
                  </HelpIconWrapper>
                </Tooltip>
              </Title>
              <Button
                type="primary"
                width="100%"
                onClick={handleCreateTeamClicked}
                styles={{
                  color: theme.colors.white,
                  backgroundColor: theme.colors.blue,
                }}
                icon={
                  <IconWrapper
                    color={
                      tenantPlan.isPremium() || tenantPlan.isEnterprise()
                        ? theme.colors.white
                        : theme.colors.yellow
                    }
                  >
                    <PremiumIconFilled />
                  </IconWrapper>
                }
              >
                {t('Create team')}
              </Button>
            </ShareMenuItemContainer>
          )}
          <Divider />
          <Button
            onClick={handlePresent}
            loading={isCreatingGame}
            styles={{ width: '100%', marginTop: 20 }}
            icon={
              <IconWrapper
                color={
                  tenantPlan?.isPremium() || tenantPlan?.isEnterprise()
                    ? theme.colors.white
                    : theme.colors.yellow
                }
              >
                <PlayIcon />
              </IconWrapper>
            }
          >
            {t('Present')}
          </Button>
        </>
      )}
      {shouldShowDownloadAsPDF && setShouldShowDownloadAsPDF && (
        <ShareMenuItemContainer style={{ padding: 0, marginTop: '-20px' }}>
          <DownloadButton onClick={() => setShouldShowDownloadAsPDF(false)}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <IconWrapper color={theme.colors.black} height={'30px'} width={'30px'}>
                <ArrowLeftSvg style={{ marginLeft: '-10px' }} />
              </IconWrapper>
              <span>{t('Download as PDF')}</span>
            </div>
            {(tenantPlan?.isPremium() || tenantPlan?.isEnterprise()) && (
              <IconWrapper color={theme.colors.black} height={'16px'} width={'16px'}>
                <PremiumIconFilled />
              </IconWrapper>
            )}
          </DownloadButton>
          <Divider style={{ top: '50px' }} />
          <p style={{ marginBottom: 0, color: '#5F6368' }}>
            PDF does not support video or interactive questions. Consider{' '}
            <LinkStyleButton onClick={handlePresent} loading={isCreatingGame}>
              presenting
            </LinkStyleButton>{' '}
            to your students, or{' '}
            <LinkStyleButton id="copy_url_template" data-clipboard-text={shareUrl}>
              {copyTemplateSuccess ? 'link copied' : 'share a lesson link'}
            </LinkStyleButton>{' '}
            instead.
          </p>
          <Button
            type="primary"
            width="100%"
            loading={isDownloadingPDF}
            onClick={handleDownloadPDFClicked}
            icon={
              <IconWrapper
                color={
                  tenantPlan?.isPremium() || tenantPlan?.isEnterprise()
                    ? theme.colors.white
                    : theme.colors.yellow
                }
              >
                <PremiumIconFilled />
              </IconWrapper>
            }
          >
            {t('Download PDF')}
          </Button>
        </ShareMenuItemContainer>
      )}
    </div>
  );
};

const HelpIconWrapper = styled.span`
  cursor: pointer;
  margin-left: 5px;
  svg {
    height: 16px;
    width: 16px;
  }
`;

const LinkStyleButton = styled(Button)`
  color: ${theme.colors.black};
  background: none;
  display: inline-block;
  box-shadow: none;
  margin-bottom: -3px;
  border: none;
  border-radius: 0;
  padding: 0;

  span {
    text-decoration: underline;
  }

  &:hover {
    background: none;
    text-decoration: none;
    color: ${theme.colors.black};
    span {
      text-decoration: none;
    }
  }

  &:focus {
    background: none;
    text-decoration: none;
    color: ${theme.colors.black};
    span {
      text-decoration: none;
    }
  }
`;

const DownloadButton = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  height: 50px;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }

  span {
    display: flex;
    align-items: center;
  }
`;

const ShareMenuItemContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  padding: 20px 0px;
`;

export default CreatorShareComponent;

const SocialMediaContainer = styled.div`
  display: flex;
  padding-bottom: 0.5em;
  & > *:not(:first-child) {
    margin-left: 2px;
  }
  & > *:not(:last-child) {
    margin-right: 2px;
  }
`;
const Divider = styled.div`
  height: 1px;
  background: ${theme.colors.grey};
  width: 100%;
  position: absolute;
  left: 0;
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;
